import React, { useEffect, useState } from 'react'
import type { InputProps as MuiInputProps } from '@material-ui/core/Input'
import { makeStyles, Tooltip as MuiTooltip } from '@material-ui/core'
import type { Theme } from '@material-ui/core'
import MuiInput from '@material-ui/core/Input'
import FormHelperText from '@material-ui/core/FormHelperText'
import classnames from 'classnames'
import Typography from '../Typography'
import PsTooltip from '../Tooltip'
import type { StyleClasses } from '../../styles'
import InputLabel from '../InputLabel'

type StateProps = Pick<InputProps, 'disabled' | 'error' | 'warning'>

// Border / shadow colour for the error and warning states. `error` wins over `warning`.
const stateColor = (theme: Theme, { error, warning }: StateProps, fallback: string) => {
  if (error) return theme.palette.error.main
  if (warning) return theme.palette.warning.main
  return fallback
}

const stateBackground = (theme: Theme, { disabled, error, warning }: StateProps) => {
  if (disabled) return theme.paintscout.palette.brand.Neutral.P94
  if (error) return '#f9e6e6'
  if (warning) return theme.palette.warning.light
  return theme.palette.background.paper
}

const useStyles = makeStyles<Theme, InputProps>(
  (theme) => ({
    root: {
      ...theme.typography.body1,
      background: 'none',
      boxSizing: 'border-box',
      color: theme.palette.text.primary,
      border: `1px solid`,
      borderRadius: theme.borderRadius.md,
      transition: 'border 0.1s, box-shadow 0.1s',
      '& textarea': {
        minHeight: ({ multilineGrow }) => (multilineGrow ? 0 : '38px')
      },
      backgroundColor: (props) => stateBackground(theme, props),
      borderColor: (props) => stateColor(theme, props, theme.paintscout.palette.brand.Neutral.P85),
      borderWidth: 1,
      boxShadow: (props) => (props.error || props.warning ? `0 0 0 1px ${stateColor(theme, props, '')}` : 'none'),
      padding: ({ multilineGrow }) => (multilineGrow ? '0px !important' : 'unset'),
      '&:hover:not(:focus-within)': {
        borderColor: (props) =>
          props.disabled ? theme.paintscout.palette.brand.Neutral.P85 : stateColor(theme, props, 'transparent'),
        boxShadow: (props) =>
          props.disabled ? 'none' : `0 0 0 2px ${stateColor(theme, props, theme.paintscout.palette.brand.Neutral.P70)}`,
        boxSizing: 'border-box'
      },
      '&:focus-within': {
        borderColor: 'transparent',
        // warning keeps its amber ring while focused; error still takes the primary focus ring
        boxShadow: (props) =>
          `0 0 0 2px ${props.warning ? theme.palette.warning.main : theme.paintscout.palette.primary}`,
        backgroundColor: (props) => stateBackground(theme, props)
      }
    },
    input: {
      minHeight: 20,
      height: '100%',
      padding: '0.50em',
      // Floored at 16px regardless of theme/fontScale: below this, iOS Safari auto-zooms
      // the viewport on focus and `user-scalable=no` (apps/app/index.html) blocks zooming back out.
      // This intentionally overrides a company's fontScale for input text specifically —
      // the 16px floor is an iOS constraint, not a branding choice, so it applies uniformly.
      // max() (not a flat 16px) still honors a user's browser-level text-size preference above 100%.
      fontSize: 'max(16px, 1rem)',
      color: ({ disabled }) => (disabled ? theme.palette.text.disabled : theme.palette.text.primary),
      '&::placeholder': {
        opacity: 1,
        color: theme.paintscout.palette.brand.Neutral.P80
      }
    },
    sublabel: {},
    focused: {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
    },
    wrapper: (props) => ({
      ...(props.fullWidth && { flex: 1 })
    }),
    inlineWrapper: {
      display: 'flex',
      flexDirection: 'row',
      '& label': {
        position: 'static',
        margin: 0
      },
      '& $labelWrapper': {
        paddingTop: theme.spacing(2)
      },
      '& $inputWrapper > div': {
        marginTop: 0
      }
    },
    labelWrapper: {},
    labelSubWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& > label': {
        color: 'inherit !important'
      }
    },
    reset: {
      cursor: 'pointer',
      lineHeight: 1,
      fontSize: '.75rem'
    },
    inputWrapper: (props) => ({
      ...((props.label || props.sublabel) && {
        marginTop: theme.typography.pxToRem(5)
      })
    }),
    hasLabel: {},
    hasSublabel: {},
    helperText: {
      marginTop: 0
    },
    warningHelperText: {
      color: theme.palette.warning.dark,
      // gap so the text clears the 2px amber ring under the field
      marginTop: theme.spacing(0.5)
    },
    toolTipDiv: {
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'flex-start'
    },
    toolTipLabel: {
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    inputMultiline: ({ multilineGrow }) => {
      if (!multilineGrow) return {}
      return {
        transition: 'min-height 0.3s ease',
        '&:focus': {
          minHeight: 45
        }
      }
    },
    required: {
      color: theme.palette.error.main
    },
    error: {}
  }),
  { name: 'Input' }
)

// Note: `inputProps` (from `MuiInputProps`) passes attributes straight to the native element,
// so a caller supplying `inputProps={{ style: { fontSize: ... } }}` can override the 16px
// iOS-zoom-prevention floor set in this component's `input` class. Avoid doing that.
export interface InputProps extends MuiInputProps {
  classes?: MuiInputProps['classes'] & StyleClasses<typeof useStyles>
  ref?: React.Ref<HTMLInputElement>

  label?: React.ReactNode
  sublabel?: React.ReactNode
  inputContent?: React.ReactNode
  inlineLabel?: boolean
  helperText?: React.ReactNode
  helperTextState?: 'disabled' | 'error' | 'filled' | 'focused' | 'warning'
  onReset?: () => any
  showReset?: boolean

  form?: any
  format?: string
  toolTip?: string | React.ReactNode
  showErrorBeforeValidation?: boolean
  multilineGrow?: boolean
  /** Advisory state: amber field and helper text. Unlike `error`, it does not block anything. `error` wins if both are set. */
  warning?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(props: InputProps, ref) {
  const [showError, setShowError] = useState(!!props.showErrorBeforeValidation)
  const error = props.error && showError
  const warning = !error && !!props.warning
  const classes = useStyles({ ...props, error, warning })

  useEffect(() => {
    if (props.form && props.form.submitCount > 0 && !props.form.isValid) {
      setShowError(true)
    }
  }, [props.form])

  useEffect(() => {
    setShowError(!!props.showErrorBeforeValidation)
  }, [props.showErrorBeforeValidation])

  const {
    toolTip = null,
    inlineLabel,
    helperText,
    helperTextState: _helperTextState,
    showReset,
    onReset,
    form: _form,
    format,
    showErrorBeforeValidation: _showErrorBeforeValidation,
    multilineGrow: _multilineGrow,
    sublabel,
    label,
    inputContent: _inputContent,
    warning: _warning,
    ...restProps
  } = props

  // eslint-disable-next-line prefer-const
  let inputProps = { ...restProps }

  // Make sure price fields are correctly formatted
  if (format === 'price') {
    inputProps = {
      ...inputProps,
      value: Number(inputProps.value).toFixed(2)
    }
  }

  const FullInputLabel = toolTip ? (
    <div className={classes.toolTipDiv}>
      <Typography className={classes.toolTipLabel} variant="overline">
        {label} {props.required && <span className={classes.required}>*</span>}
      </Typography>
      <PsTooltip interactive={typeof toolTip !== 'string'} content={toolTip} className={classes.toolTip} />
    </div>
  ) : (
    <InputLabel shrink={false} required={props.required} disabled={props.disabled}>
      {label}
    </InputLabel>
  )

  const labelContent = label ? FullInputLabel : null
  const sublabelContent = sublabel ? (
    <InputLabel className={classes.sublabel} variant={'sublabel'}>
      {sublabel}
    </InputLabel>
  ) : null

  const helperTextState = error ? 'error' : warning ? 'warning' : _helperTextState
  const requiredError = props.required && error && helperTextState === 'error' && !helperText
  const helperTextContent =
    helperText || requiredError ? (
      <FormHelperText
        classes={{ root: classes.helperText }}
        className={classnames(classes.helperText, { [classes.warningHelperText]: helperTextState === 'warning' })}
        disabled={helperTextState === 'disabled'}
        error={helperTextState === 'error'}
        filled={helperTextState === 'filled'}
        focused={helperTextState === 'focused'}
      >
        {requiredError ? `This field is required.` : helperText}
      </FormHelperText>
    ) : null

  return (
    <div className={classnames({ [classes.wrapper]: true, [classes.inlineWrapper]: inlineLabel })}>
      {(labelContent || sublabelContent) && (
        <div className={classes.labelWrapper}>
          <div className={classes.labelSubWrapper}>
            {labelContent && labelContent}
            {showReset && (
              <span
                className={classes.reset}
                onClick={(ev) => {
                  ev.preventDefault()
                  onReset()
                }}
              >
                <MuiTooltip title={'Reset to default (generated) value'}>
                  <span>
                    <Typography variant={'overline'}>{'(Reset)'}</Typography>
                  </span>
                </MuiTooltip>
              </span>
            )}
          </div>
          {sublabelContent && sublabelContent}
        </div>
      )}
      <div className={classes.inputWrapper}>
        <MuiInput
          {...inputProps}
          error={error}
          onInvalid={(e) => {
            e.preventDefault()
            setShowError(true)
          }}
          classes={{
            root: classes.root,
            input: classes.input,
            focused: classes.focused,
            inputMultiline: classes.inputMultiline
          }}
          inputRef={ref}
        />
        {helperTextContent}
      </div>
    </div>
  )
})

Input.defaultProps = {
  disableUnderline: true
}

export default Input
